import { isArray } from 'lodash';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';
import UINineSliceComponent from '../../../../Component/Render/NineSlice';
import UITextComponent from '../../../../Component/UI/Text';
import Game from '../../../../Game';
import Element from '../../../../Prototype/UI/Element';
import { IUIElementLayout } from '../../../../Prototype/UI/UIItem';
import { IPlayerStateQuestData } from '../../../../State/Player/IPlayerState';
import { MathHelper } from '../../../../Utility/MathHelper';
import QuestHelper from '../../../../Utility/QuestHelper';

const progressBarFillMaxWidth: number = 251;
export default class QuestButtonProgress extends Element {
    private target: number;
    private progressTextComponent: UITextComponent;
    public fillNineSlice: UINineSliceComponent;

    public create(params: {
        name?: string;
        layout?: Partial<IUIElementLayout>;
        questData: IPlayerStateQuestData;
        alpha: number;
    }): void {
        const hasXmasEvent = EventStateHelper.getEventState(Game.store.getState(), EventType.XMAS_2021);
        this.target = QuestHelper.getFinalQuestTarget(
            params.questData.type,
            params.questData.tier,
            QuestHelper.getQuestPoolKeyByEventType(params.questData.eventType)
        );
        super.create({
            name: params.name,
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
                ...params.layout,
            },
            components: [
                {
                    type: 'transparency',
                    data: {
                        alpha: params.alpha,
                    },
                },
                {
                    type: 'image',
                    data: {
                        cacheKey: 'mission_bar_back',
                    },
                },
            ],
            children: [
                {
                    name: 'title',
                    layout: {
                        anchors: [0.5, 0, 0.5, 0],
                        offsets: [0, 0, 0, 0],
                        pivot: [0.5, 1],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                textLocalizationKey: QuestHelper.getNotificationKey(
                                    params.questData.type
                                ),
                                style: hasXmasEvent ? 'events.xmas.progress_update_title' : 'quests.button.progress_update_title',
                                bounds: [225, 60],
                                wordwrap: true,
                                wordWrapWidth: 225,
                            },
                        },
                    ],
                },
                {
                    name: 'progress_bar_fill',
                    layout: {
                        anchors: [0, 0.5, 0, 0.5],
                        offsets: [-1, 0, -1, 0],
                        pivot: [0, 0.5],
                        scale: [1, 1],
                        constrains: {
                            heightMin: 41,
                            heightMax: 41,
                            widthMin: 0,
                            widthMax: progressBarFillMaxWidth,
                        },
                    },
                    components: [
                        {
                            type: 'nineslice',
                            data: {
                                cacheKey: 'mission_bar_blue',
                                offset: [16, 16, 16, 16],
                                // TODO: This isn't actually setting the width
                                width: this.getWidthFromProgress(
                                    params.questData.progress
                                ),
                            },
                        },
                        {
                            type: 'texture_mask',
                            data: {
                                maskCacheKey: 'mission_bar_mask',
                            },
                        },
                    ],
                },
                {
                    name: 'progress_bar_foreground',
                    layout: {
                        anchors: [0.5, 0.5, 0.5, 0.5],
                        offsets: [0, 0, 0, 0],
                        pivot: [0.5, 0.5],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: 'mission_bar_front',
                            },
                        },
                    ],
                },
                {
                    name: 'progress_bar_text',
                    layout: {
                        anchors: [0.5, 0.5, 0.5, 0.5],
                        offsets: [0, 0, 0, 0],
                        pivot: [0.5, 0.5],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                text: `${this.clampValue(
                                    params.questData.progress
                                )} / ${this.target}`,
                                bounds: [200, 75],
                                style: 'quests.button.progress_update_bar',
                            },
                        },
                    ],
                },
            ],
            ...params,
        });
        this.progressTextComponent = this.progressBarText.getComponent(
            UITextComponent
        );
        this.fillNineSlice = this.progressBarFill.getComponent(
            UINineSliceComponent
        );
    }

    public updateText(count: number) {
        this.progressTextComponent.text = `${this.clampValue(count).toFixed(
            0
        )} / ${this.target}`;
    }
    /**
     * Gets the width of the progressbar for the given progress
     * @progress base quest progress not % progress
     */
    public getWidthFromProgress(progress: number): number {
        // Clamp and round progress
        const progressInt = Math.floor(this.clampValue(progress));

        // Get % of bar to fill
        const percProgress = MathHelper.normalize(progressInt, 0, this.target);
        // Interpolate between max and min width based off the % progress to get the new width
        return Math.min(progressBarFillMaxWidth, MathHelper.lerp(0, progressBarFillMaxWidth, percProgress));
    }
    private clampValue(value: number): number {
        return MathHelper.clamp(value, 0, this.target);
    }

    public get progressBarFill() {
        return this.find('progress_bar_fill');
    }
    public get progressBarText() {
        return this.find('progress_bar_text');
    }
}
